import React, { useReducer } from 'react';
import PropTypes from 'prop-types';
import { Modal, View } from 'react-native';
import { noop } from 'Utils';
import { t } from 'Locale';
import { PinInput, ModalHeader } from 'LocalGluons';
import { useStore } from 'Store';

import { SuccessPage } from '../../../VerificationCodePages';

import defaultStyles from './ChangePinModal.jss';

const PAGES = Object.freeze({
    enterPin: 'enterPin',
    newPin: 'newPin',
    newPinAgain: 'newPinAgain',
    pinChangedSuccess: 'pinChangedSuccess'
});

const initState = {
    oldPin: '',
    newPin: '',
    page: PAGES.enterPin,
    errorMessage: ''
};

const ACTIONS = Object.freeze({
    setOldPin: 'setOldPin',
    setNewPin: 'setNewPin',
    setNewPage: 'setNewPage'
});

function reducer(state, action) {
    switch (action.type) {
        case ACTIONS.setOldPin:
            if (action.value) {
                return {
                    ...state,
                    oldPin: action.value
                };
            }
            break;
        case ACTIONS.setNewPin:
            if (action.value) {
                return {
                    ...state,
                    newPin: action.value
                };
            }
            break;
        case ACTIONS.setNewPage:
            if (action.value) {
                return {
                    ...state,
                    errorMessage: action.errorMessage || '',
                    page: action.value
                };
            }
            break;
        default:
            break;
    }
    return state;
}

const ChangePinModal = ({ modalVisible, onCancel, pinValidateFunction, onForgotPinPressed }) => {
    const [state, dispatch] = useReducer(reducer, { ...initState });

    const { appManager } = useStore();

    const changePinValidationFunction = async (value) => {
        switch (state.page) {
            case PAGES.enterPin:
                dispatch({ type: ACTIONS.setOldPin, value });
                return pinValidateFunction(value);
            case PAGES.newPin:
                dispatch({ type: ACTIONS.setNewPin, value });
                break;
            case PAGES.newPinAgain:
                if (state.newPin !== value) {
                    return {
                        errorMessage: t('SETTINGS.CHANGE_PARENTAL_PIN_WRONG_MSG')
                    };
                }
                return appManager.api.changePin(state.oldPin, state.newPin);
            default:
                break;
        }
        return false;
    };

    const pageComponents = {
        [PAGES.enterPin]: (
            <PinInput
                key="pin-input-0"
                header={t('SETTINGS.CHANGE_PARENTAL_PIN_ENTER_CURRENT_PIN')}
                message={t('SETTINGS.CHANGE_PARENTAL_PIN_ENTER_CURRENT_PIN_INFO')}
                pinValidateFunction={changePinValidationFunction}
                onForgotPinPressed={onForgotPinPressed}
                onPinSuccess={() => dispatch({ type: ACTIONS.setNewPage, value: PAGES.newPin })}
            />
        ),
        [PAGES.newPin]: (
            <PinInput
                key="pin-input-1"
                header={t('SETTINGS.PARENTAL_NEW')}
                onPinSuccess={() =>
                    dispatch({ type: ACTIONS.setNewPage, value: PAGES.newPinAgain })
                }
                pinValidateFunction={changePinValidationFunction}
                invalidPinMessage={state.errorMessage}
            />
        ),
        [PAGES.newPinAgain]: (
            <PinInput
                key="pin-input-2"
                header={t('SETTINGS.PARENTAL_NEW_CONFIRM')}
                pinValidateFunction={changePinValidationFunction}
                onPinSuccess={() => {
                    dispatch({ type: ACTIONS.setNewPage, value: PAGES.pinChangedSuccess });
                }}
                onPinFailed={(message) => {
                    dispatch({
                        type: ACTIONS.setNewPage,
                        value: PAGES.newPin,
                        errorMessage: message
                    });
                }}
            />
        ),
        [PAGES.pinChangedSuccess]: (
            <SuccessPage message={t('COMMON.PIN_CHANGE_SUCCESS')} onContinue={onCancel} />
        )
    };

    return (
        <Modal animationType="slide" transparent visible={modalVisible} onRequestClose={onCancel}>
            <View style={[defaultStyles.container, onCancel ? defaultStyles.backdrop : {}]}>
                {onCancel && <ModalHeader onPress={onCancel} />}
                {pageComponents[state.page]}
            </View>
        </Modal>
    );
};

ChangePinModal.defaultProps = {
    modalVisible: false,
    onCancel: noop,
    onForgotPinPressed: noop
};

ChangePinModal.propTypes = {
    modalVisible: PropTypes.bool,
    onCancel: PropTypes.func,
    pinValidateFunction: PropTypes.func.isRequired,
    onForgotPinPressed: PropTypes.func
};

export { ChangePinModal };
